/**
 * Servicio de autenticación que usa la nueva API backend
 * Reemplaza el clerkUserService para usar endpoints centralizados
 */

import { apiHelpers } from './normalizedApiClient';
import { API_ENDPOINTS } from '../config/api';
import { normalizeDataArray, normalizeDataObject } from '../utils/normalizationUtils';
import { usuarioTransforms, clienteTransforms } from '../utils/dataTransforms';

/**
 * Obtiene o crea el usuario usando la nueva API backend
 * Este endpoint maneja toda la lógica de verificación/creación automáticamente
 */
export const getOrCreateUserProfile = async () => {
  try {
    console.log('🔐 [AuthService] Obteniendo datos');
    console.log('🔐 [AuthAPI] Obteniendo perfil de usuario con nueva API...');
    
    // Obtener tipo de usuario desde localStorage para enviarlo al backend
    const userType = localStorage.getItem('revvly_user_type');
    console.log('👤 [AuthAPI] Tipo de usuario desde localStorage:', userType);
    
    // Enviar tipo de usuario como header
    const headers = {};
    if (userType) {
      headers['x-user-type'] = userType;
    }
    
    const response = await apiHelpers.usuarios.get(API_ENDPOINTS.AUTH.ME, { headers });
    console.log('🔗 [AuthAPI] Respuesta completa de API:', response);
    
    if (response.data) {
      console.log('✅ [AuthAPI] Usuario obtenido exitosamente:', response.data);
      
      // Extraer el objeto usuario de la respuesta
      const userData = response.data.usuario || response.data;
      console.log('📤 [AuthAPI] Datos de usuario extraídos:', userData);
      
      // Transformar datos para compatibilidad con el frontend actual
      const transformedUser = usuarioTransforms.fromApi(userData);
      console.log('🔄 [AuthAPI] Usuario transformado final:', transformedUser);
      
      return transformedUser;
    }
    
    console.log('⚠️ [AuthAPI] No hay datos en la respuesta');
    return null;
  } catch (error) {
    console.error('❌ [AuthAPI] Error al obtener perfil de usuario:', error);
    console.error('❌ [AuthAPI] Error details:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    
    // Si es error 401, el token no es válido
    if (error.response?.status === 401) {
      console.warn('🔒 [AuthAPI] Token inválido, usuario no autenticado');
      return null;
    }
    
    throw error;
  }
};

/**
 * Registra un nuevo usuario (solo para casos especiales)
 * La mayoría de usuarios se crean automáticamente con GET /api/auth/me
 */
export const registerUser = async (userData) => {
  try {
    //console.log('🔐 [AuthService] Procesando operación');
    //console.log('📝 [AuthAPI] Registrando nuevo usuario:', userData);
    
    const transformedData = usuarioTransforms.toApi(userData);
    const response = await apiHelpers.usuarios.post(API_ENDPOINTS.AUTH.REGISTER, transformedData);
    
    if (response.data) {
      //console.log('✅ [AuthAPI] Usuario registrado exitosamente');
      return usuarioTransforms.fromApi(response.data);
    }
    
    return null;
  } catch (error) {
    console.error('❌ [AuthAPI] Error al registrar usuario:', error);
    throw error;
  }
};

/**
 * Busca un usuario por su ID de Clerk usando la nueva API
 */
export const getUserByClerkId = async (clerkId) => {
  try {
    //console.log('🔐 [AuthService] Obteniendo datos');
    //console.log('🔍 [AuthAPI] Buscando usuario por Clerk ID:', clerkId);
    
    const response = await apiHelpers.usuarios.get(API_ENDPOINTS.USUARIOS.BY_CLERK_ID(clerkId));
    
    if (response.data) {
      return usuarioTransforms.fromApi(response.data);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      // Usuario no encontrado es un caso normal
      //console.log('ℹ️ [AuthAPI] Usuario no encontrado por Clerk ID:', clerkId);
      return null;
    }
    
    console.error('❌ [AuthAPI] Error al buscar usuario por Clerk ID:', error);
    throw error;
  }
};

/**
 * Obtiene datos del cliente asociado al usuario
 */
export const getClienteData = async (clerkId) => {
  try {
    //console.log('🔐 [AuthService] Obteniendo datos');
    //console.log('👤 [AuthAPI] Obteniendo datos de cliente por Clerk ID:', clerkId);
    
    const response = await apiHelpers.usuarios.get(API_ENDPOINTS.CLIENTES.BY_CLERK_ID(clerkId));
    
    if (response.data) {
      return clienteTransforms.fromApi(response.data);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      // Cliente no encontrado es un caso normal para usuarios Admin
      //console.log('ℹ️ [AuthAPI] Cliente no encontrado por Clerk ID:', clerkId);
      return null;
    }
    
    console.error('❌ [AuthAPI] Error al obtener datos de cliente:', error);
    throw error;
  }
};

/**
 * Verifica si el usuario es super admin
 */
export const verificarSuperAdmin = async () => {
  try {
    //console.log('🔐 [AuthService] Procesando operación');
    //console.log('🔒 [AuthAPI] Verificando permisos de super admin...');
    
    const response = await apiHelpers.usuarios.get(API_ENDPOINTS.USUARIOS.SUPER_ADMIN_VERIFY);
    
    return response.data?.isSuperAdmin || false;
  } catch (error) {
    if (error.response?.status === 403) {
      // No es super admin
      return false;
    }
    
    console.error('❌ [AuthAPI] Error al verificar super admin:', error);
    return false;
  }
};

/**
 * Función principal que reemplaza createOrUpdateUserProfile
 * Simplifica el flujo usando la nueva API que maneja todo automáticamente
 */
export const createOrUpdateUserProfile = async () => {
  try {
    //console.log('🔐 [AuthService] Creando registro');
    //console.log('🔄 [AuthAPI] Iniciando flujo de crear/actualizar perfil...');
    
    // La nueva API maneja automáticamente la creación/actualización
    // Solo necesitamos llamar a GET /api/auth/me
    const user = await getOrCreateUserProfile();
    
    if (!user) {
      throw new Error('No se pudo obtener o crear el perfil de usuario');
    }
    
    return user;
  } catch (error) {
    console.error('❌ [AuthAPI] Error en createOrUpdateUserProfile:', error);
    throw error;
  }
};

/**
 * Hook personalizado para usar el servicio de auth
 */
export const useAuthApiService = () => {
  return {
    getOrCreateUserProfile,
    registerUser,
    getUserByClerkId,
    getClienteData,
    verificarSuperAdmin,
    createOrUpdateUserProfile
  };
};